import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download as DownloadIcon, Star as StarIcon, Check as CheckIcon, Gift as GiftIcon, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PLAN_NAMES = {
  free: 'Gratuit', starter: 'Starter', standard: 'Standard', pro: 'Pro', unlimited: 'Illimité'
};

export default function SubscriptionDialog({
  open, onOpenChange,
  subscriptionPlan, subscriptionPlanName, isUnlimited,
  downloadsRemaining, downloadsLimit, extraDownloads, daysUntilRenewal,
  pendingPlanChange,
  subscriptionPlans, loadingPlans, loadSubscriptionPlans,
  loadSubscriptionStatus
}) {

  const handleChangePlan = async (planId) => {
    try {
      const response = await axios.post(
        `${API}/subscription/change-plan`,
        { plan_id: planId },
        { withCredentials: true }
      );
      if (response.data.days_remaining) {
        toast.success(`Vous passerez au forfait gratuit dans ${response.data.days_remaining} jours. Vous gardez vos téléchargements actuels jusqu'à la fin du cycle.`);
      } else {
        toast.success("Changement programmé pour la fin du cycle!");
      }
      loadSubscriptionStatus();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du changement");
    }
  };

  const handleSubscribe = async (planId) => {
    try {
      toast.info("Redirection vers PayPal...");
      const response = await axios.post(
        `${API}/subscription/create`,
        { 
          plan_id: planId,
          return_url: window.location.origin + '/dashboard?subscription=success',
          cancel_url: window.location.origin + '/dashboard?subscription=cancelled'
        },
        { withCredentials: true }
      );
      window.location.href = response.data.approve_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur PayPal. Veuillez réessayer.");
    }
  };

  const handleBuyExtra = async (quantity, price) => {
    try {
      toast.info("Redirection vers PayPal...");
      const response = await axios.post(
        `${API}/subscription/create-extra-order`,
        { 
          quantity, price,
          return_url: window.location.origin + '/dashboard?extra=success&qty=' + quantity,
          cancel_url: window.location.origin + '/dashboard?extra=cancelled'
        },
        { withCredentials: true }
      );
      window.location.href = response.data.approve_url;
    } catch (error) {
      toast.error("Erreur PayPal");
    }
  };

  const handleCancel = async () => {
    if (window.confirm("Êtes-vous sûr de vouloir annuler votre abonnement? Vous garderez l'accès jusqu'à la fin de votre cycle actuel.")) {
      try {
        await axios.post(`${API}/subscription/cancel`, {}, { withCredentials: true });
        toast.success("Abonnement annulé. Vous garderez l'accès jusqu'à la fin du cycle.");
        loadSubscriptionStatus();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Erreur lors de l'annulation");
      }
    }
  };

  const extraPacks = [
    { quantity: 5, price: 2.99 },
    { quantity: 15, price: 6.99 },
    { quantity: 30, price: 11.99 }
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
        <DialogHeader>
          <DialogTitle data-testid="subscription-dialog-title" style={{ fontSize: '1.5rem', color: '#5D6F61', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
            <DownloadIcon size={24} color="#5D6F61" /> Gérer mon abonnement
          </DialogTitle>
        </DialogHeader>
        
        <div style={{ padding: '1rem' }}>
          {/* Status actuel */}
          <div style={{
            background: isUnlimited 
              ? 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)' 
              : 'linear-gradient(135deg, #87A96B 0%, #9CAF88 100%)',
            padding: '1.5rem', borderRadius: '16px', textAlign: 'center', marginBottom: '1.5rem', color: 'white'
          }}>
            <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem', opacity: 0.9 }}>Votre forfait actuel</div>
            <div style={{ fontSize: '1.8rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>{subscriptionPlanName}</div>
            <div style={{ fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
              <DownloadIcon size={20} />
              {isUnlimited ? (
                <span>Vidéos recettes illimitées</span>
              ) : (
                <span>{downloadsRemaining + extraDownloads}/{downloadsLimit} vidéos recettes restantes</span>
              )}
            </div>
            <div style={{ fontSize: '0.85rem', marginTop: '0.75rem', opacity: 0.8 }}>
              Renouvellement dans {daysUntilRenewal} jours
            </div>
            {pendingPlanChange && (
              <div style={{ marginTop: '0.75rem', padding: '0.5rem', background: 'rgba(255,255,255,0.2)', borderRadius: '8px', fontSize: '0.85rem' }}>
                Passage à "{PLAN_NAMES[pendingPlanChange] || pendingPlanChange}" prévu au prochain cycle
              </div>
            )}
          </div>

          {/* Forfaits */}
          <h3 style={{ fontSize: '1.1rem', color: '#5D6F61', marginBottom: '1rem', textAlign: 'center' }}>Choisir un forfait</h3>
          
          {loadingPlans ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}><Loader2 className="animate-spin" size={32} style={{ color: '#87A96B' }} /></div>
          ) : subscriptionPlans.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
              <p>Impossible de charger les forfaits.</p>
              <button onClick={loadSubscriptionPlans} style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: '#87A96B', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Réessayer</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {subscriptionPlans.map((plan) => (
                <div key={plan.id} data-testid={`plan-${plan.id}`} style={{
                  padding: '1rem',
                  background: plan.popular ? 'linear-gradient(135deg, #87A96B 0%, #9CAF88 100%)' : 'white',
                  border: subscriptionPlan === plan.id ? '3px solid #FFD700' : (plan.popular ? 'none' : '2px solid #E8E4D9'),
                  borderRadius: '12px', position: 'relative'
                }}>
                  {plan.popular && (
                    <div style={{ position: 'absolute', top: '-8px', right: '12px', background: '#FFD700', color: '#8B7355', padding: '0.25rem 0.75rem', borderRadius: '12px', fontSize: '0.7rem', fontWeight: 'bold' }}>
                      <StarIcon size={14} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '0.25rem' }} />POPULAIRE
                    </div>
                  )}
                  {subscriptionPlan === plan.id && (
                    <div style={{ position: 'absolute', top: '-8px', left: '12px', background: '#4CAF50', color: 'white', padding: '0.25rem 0.75rem', borderRadius: '12px', fontSize: '0.7rem', fontWeight: 'bold' }}>
                      <CheckIcon size={14} style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '0.25rem' }} />ACTUEL
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <DownloadIcon size={24} color={plan.popular ? 'white' : '#5D6F61'} />
                      <div>
                        <div style={{ fontWeight: 'bold', fontSize: '1.1rem', color: plan.popular ? 'white' : '#5D6F61' }}>{plan.name}</div>
                        <div style={{ fontSize: '0.85rem', color: plan.popular ? 'rgba(255,255,255,0.8)' : '#8B7355' }}>
                          {plan.downloads_per_month === -1 ? 'Vidéos recettes illimitées' : `Maximum ${plan.downloads_per_month} vidéos recettes/mois`}
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '1.3rem', fontWeight: 'bold', color: plan.popular ? 'white' : '#87A96B' }}>
                        {plan.price === 0 ? 'Gratuit' : `$${(plan.price * 1.14975).toFixed(2)}/mois`}
                      </div>
                      {plan.price > 0 && (
                        <div style={{ fontSize: '0.7rem', color: plan.popular ? 'rgba(255,255,255,0.7)' : '#999', marginTop: '0.1rem' }}>
                          {`$${plan.price.toFixed(2)} + $${(plan.price * 0.14975).toFixed(2)} taxes`}
                        </div>
                      )}
                    </div>
                  </div>
                  {subscriptionPlan !== plan.id && (
                    plan.price === 0 ? (
                      <Button onClick={() => handleChangePlan(plan.id)} data-testid={`btn-plan-${plan.id}`}
                        style={{ width: '100%', background: '#E8E4D9', color: '#5D6F61', border: 'none' }}>
                        Passer au gratuit
                      </Button>
                    ) : (
                      <div style={{ marginTop: '0.5rem' }}>
                        <Button onClick={() => handleSubscribe(plan.id)} data-testid={`btn-plan-${plan.id}`}
                          style={{ width: '100%', background: plan.popular ? 'white' : '#FFC439', color: plan.popular ? '#87A96B' : '#2C2E2F', border: 'none', fontWeight: 'bold', padding: '0.75rem', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/>
                          </svg>
                          S'abonner avec PayPal
                        </Button>
                      </div>
                    )
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Packs bonus */}
          <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#FFF9E6', borderRadius: '12px', border: '2px dashed #D4A574' }}>
            <h4 style={{ fontSize: '1rem', color: '#8B7355', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <GiftIcon size={18} color="#8B7355" /> Besoin de plus? Achetez des vidéos recettes bonus
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {extraPacks.map((pack) => (
                <div key={pack.quantity} data-testid={`extra-pack-${pack.quantity}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem', background: 'white', borderRadius: '8px', border: '1px solid #E8E4D9' }}>
                  <div>
                    <span style={{ fontWeight: '600', color: '#5D6F61' }}>{pack.quantity} vidéos recettes</span>
                    <span style={{ color: '#8B7355', marginLeft: '0.5rem' }}>${(pack.price * 1.14975).toFixed(2)}</span>
                    <div style={{ fontSize: '0.65rem', color: '#999' }}>
                      {`$${pack.price.toFixed(2)} + $${(pack.price * 0.14975).toFixed(2)} taxes`}
                    </div>
                  </div>
                  <Button onClick={() => handleBuyExtra(pack.quantity, pack.price)}
                    style={{ background: '#FFC439', color: '#2C2E2F', border: 'none', fontWeight: 'bold', padding: '0.5rem 1rem', borderRadius: '20px', cursor: 'pointer', fontSize: '0.85rem' }}>
                    Acheter
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* Annuler */}
          {subscriptionPlan !== 'free' && (
            <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
              <button onClick={handleCancel} data-testid="cancel-subscription-btn"
                style={{ background: 'transparent', border: 'none', color: '#DC3545', cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'underline' }}>
                Annuler mon abonnement
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
